import type { AppLocale } from "@/core/i18n/config";
import type { BlogCategory, BlogPost } from "../types/blog";

export const getBlogCategories = (locale: AppLocale): BlogCategory[] => [
  {
    id: "somatic",
    name: locale === "hi" ? "सॉमेटिक" : "Somatic",
    imageUrl:
      "https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["सॉमेटिक", "हीलिंग"] : ["Somatic", "Healing"],
  },
  {
    id: "anxiety",
    name: locale === "hi" ? "चिंता" : "Anxiety",
    imageUrl:
      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["चिंता", "माइंडफुलनेस"] : ["Anxiety", "Mindfulness"],
  },
  {
    id: "trauma",
    name: locale === "hi" ? "आघात" : "Trauma",
    imageUrl:
      "https://images.unsplash.com/photo-1604871000636-074fa5117945?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["ट्रॉमा", "रिकवरी"] : ["Trauma", "Recovery"],
  },
  {
    id: "burnout",
    name: locale === "hi" ? "बर्नआउट" : "Burnout",
    imageUrl:
      "https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["बर्नआउट", "आराम"] : ["Burnout", "Rest"],
  },
  {
    id: "mindfulness",
    name: locale === "hi" ? "माइंडफुलनेस" : "Mindfulness",
    imageUrl:
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["ध्यान", "शांति"] : ["Meditation", "Calm"],
  },
  {
    id: "depression",
    name: locale === "hi" ? "अवसाद" : "Depression",
    imageUrl:
      "https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["सपोर्ट", "हीलिंग"] : ["Support", "Healing"],
  },
  {
    id: "sleep",
    name: locale === "hi" ? "नींद" : "Sleep",
    imageUrl:
      "https://images.unsplash.com/photo-1520206183501-b80af970d40e?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["गहरी नींद", "आराम"] : ["Restful Sleep", "Calm"],
  },
  {
    id: "selfcare",
    name: locale === "hi" ? "आत्म-देखभाल" : "Self-Care",
    imageUrl:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=400&q=80",
    tags: locale === "hi" ? ["आत्म-प्रेम", "रूटीन"] : ["Self-Love", "Daily"],
  },
];

export const BLOGS_DATA: Record<AppLocale, BlogPost[]> = {
  en: [
    {
      slug: "healing-from-anxiety",
      title: "The Art of Slowing Down: A Mindful Approach to Anxiety",
      excerpt:
        "Understanding anxiety not as an enemy to defeat, but as a messenger calling for gentle attention and somatic space.",
      category: "Anxiety",
      readTime: "6 min read",
      date: "May 20, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80",
      tags: ["Anxiety", "Mindfulness", "Self-Care"],
      author: {
        name: "Dr. Alisha Sen",
        role: "Clinical Psychologist & Healer",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "Over 12 years of helping individuals heal anxiety, soothe their nervous systems, and build resilient paths to self-discovery.",
        specialization: "Anxiety & Trauma Care",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "Anxiety is one of the most common human experiences, yet it is often the most misunderstood. When the chest tightens, the heart races, and thoughts spin in endless loops, our immediate instinct is to fight. We want to crush the feeling, distract ourselves, or rationalize it away. But what if we took a different path? What if we treated anxiety not as an intruder to lock out, but as a messenger knocking loudly on the door?",
        },
        {
          type: "heading",
          level: 2,
          text: "1. Greeting the Wave Without Drowning",
        },
        {
          type: "paragraph",
          text: "Slowing down with anxiety starts with validation. When we resist our anxiety, we create a secondary layer of panic—we become anxious about being anxious. Somatic healing teaches us to acknowledge the wave as it rises. By pausing and saying to ourselves, 'I feel anxiety in my body right now, and that is okay,' we begin to untangle our identity from the sensation. You are not the anxiety; you are the ocean experiencing a passing wave.",
        },
        {
          type: "pullquote",
          text: "We don't try to stop the storm. We simply learn to rest in the quiet spaces between the gusts, knowing that every wind eventually runs out of breath.",
        },
        {
          type: "heading",
          level: 2,
          text: "2. The Physiology of the Pause",
        },
        {
          type: "paragraph",
          text: "Our nervous system operates in shifts. When anxiety takes over, the sympathetic branch (fight-or-flight) is in control. To restore balance, we must invite the parasympathetic branch (rest-and-digest) to step in. This is not achieved by logical thinking; it is achieved through the body. A simple, extended exhale—breathing out longer than you breathe in—signals to the brain's alarm center that you are safe in this exact moment.",
        },
        {
          type: "heading",
          level: 2,
          text: "3. Three Gentle Grounding Rituals",
        },
        {
          type: "paragraph",
          text: "When you feel your mind drifting into future worries, try these somatic practices to anchor yourself back to the present:",
        },
        {
          type: "list",
          items: [
            "The 5-4-3-2-1 Somatic Check: Name five things you can see, four you can touch, three you can hear, two you can smell, and one you can taste.",
            "Weight Sensing: Sit back, close your eyes, and feel the heavy, secure pressure of your feet connecting with the ground and your hips resting in your chair.",
            "Warm Palm Hold: Rub your palms together until they generate heat, then place them gently over your heart or stomach, breathing into the warmth.",
          ],
        },
        {
          type: "paragraph",
          text: "Anxiety does not disappear overnight, and the goal of healing is not to live in static perfection. The goal is to build a softer relationship with your struggles, learning to walk alongside them with grace, patience, and absolute self-compassion.",
        },
      ],
      relatedSlugs: ["recovering-from-burnout", "healing-relational-trauma"],
    },
    {
      slug: "somatic-practices-nervous-system",
      title: "Somatic Safety: 3 Exercises to Quiet a Hypervigilant Body",
      excerpt:
        "Moving beyond talk therapy by using body-based techniques to re-establish physiological safety, ground your nervous system, and rest.",
      category: "Somatic",
      readTime: "5 min read",
      date: "May 18, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1200&q=80",
      tags: ["Somatic", "Nervous System", "Healing"],
      author: {
        name: "Dr. Alisha Sen",
        role: "Clinical Psychologist & Healer",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "Over 12 years of helping individuals heal anxiety, soothe their nervous systems, and build resilient paths to self-discovery.",
        specialization: "Anxiety & Trauma Care",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "Many people spend years in cognitive talk therapy analyzing why they feel stuck, anxious, or overwhelmed. Yet, understanding our patterns logically is only half the battle. When our nervous system is in a state of high alarm, the body acts before the thinking brain can intervene. Somatic practices invite us to communicate with our system in its own language: the language of bodily sensation, breath, and movement.",
        },
        {
          type: "heading",
          level: 2,
          text: "The Body Keeps the Rhythm",
        },
        {
          type: "paragraph",
          text: "Somatic safety exercises are simple gestures designed to tell your sympathetic nervous system that the immediate danger has passed. Try these three basic tools when your heart rate speeds up or your chest starts to constrict.",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "healing-relational-trauma"],
    },
    {
      slug: "recovering-from-burnout",
      title: "Restoring the Spark: Moving from Exhaustion to Alignment",
      excerpt:
        "Burnout is not a failure of willpower or productivity; it is the soul's plea for a deeper rest that sleep alone cannot provide.",
      category: "Burnout",
      readTime: "8 min read",
      date: "May 15, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=1200&q=80",
      tags: ["Burnout", "Rest", "Wellness"],
      author: {
        name: "Kabir Mehta, LCSW",
        role: "Somatic Therapist & Counselor",
        avatarUrl:
          "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=150&q=80",
        bio: "Specialist in nervous system regulation, emotional boundaries, and stress reduction through integration of body and mind.",
        specialization: "Somatic Therapy & Burnout",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "In a world that celebrates constant hustle, feeling exhausted has become a badge of honor. We push past our physical, emotional, and mental limits in the name of achievement, until one day we wake up and realize the flame has gone out. This is burnout. It is not just feeling tired after a busy week; it is a profound state of depletion where even the things you once loved feel heavy and hollow.",
        },
        {
          type: "heading",
          level: 2,
          text: "Understanding the Seven Types of Rest",
        },
        {
          type: "paragraph",
          text: "Often, we attempt to cure burnout by sleeping for ten hours, only to wake up feeling just as exhausted. Dr. Saundra Dalton-Smith's research teaches us that sleep is not the same as rest. We require different types of rest to replenish our reserves:",
        },
        {
          type: "list",
          items: [
            "Physical Rest: Active or passive rest, such as sleep, gentle stretching, or yoga nidra.",
            "Mental Rest: Scheduled breaks throughout the day to let your brain wander without processing information.",
            "Sensory Rest: Unplugging from screens, noise, and bright lights to soothe overstimulated senses.",
            "Creative Rest: Allowing yourself to experience the beauty of nature, art, or music without any pressure to produce.",
            "Emotional Rest: Having the space to freely express your true feelings without filters or people-pleasing.",
            "Social Rest: Surrounding yourself with positive, authentic relationships that revive you instead of draining you.",
            "Spiritual Rest: Connecting with something larger than yourself, finding purpose, love, and belonging.",
          ],
        },
        {
          type: "pullquote",
          text: "Burnout is the body's boundary. When we refuse to set boundaries ourselves, our nervous system eventually steps in and sets one for us.",
        },
        {
          type: "heading",
          level: 2,
          text: "Cultivating Alignment Over Hustle",
        },
        {
          type: "paragraph",
          text: "Recovering from burnout is a journey of reclaiming your life. It requires you to look at your calendar, your commitments, and your habits with honesty. Ask yourself: 'What is taking my energy, and what is giving it back?' Start by setting tiny boundaries—saying no to one extra task, taking a five-minute screen-free walk, or checking in with your breath before answering emails. Healing is not about doing more; it is about aligning with less.",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "healing-relational-trauma"],
    },
    {
      slug: "mindfulness-beginners-guide",
      title: "The Quiet Space: Integrating Mindfulness into Daily Chaos",
      excerpt:
        "How to build small moments of mindfulness throughout your day without needing hours of silent meditation.",
      category: "Mindfulness",
      readTime: "4 min read",
      date: "May 12, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80",
      tags: ["Mindfulness", "Meditation", "Calm"],
      author: {
        name: "Kabir Mehta, LCSW",
        role: "Somatic Therapist & Counselor",
        avatarUrl:
          "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=150&q=80",
        bio: "Specialist in nervous system regulation, emotional boundaries, and stress reduction through integration of body and mind.",
        specialization: "Somatic Therapy & Burnout",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "Mindfulness is often pictured as sitting cross-legged on top of a misty mountain, eyes closed, thoughts completely blank. For most of us, this is simply not possible. True mindfulness does not require you to escape your life; it asks you to join it with your whole presence, in the middle of whatever you are doing.",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "recovering-from-burnout"],
    },
    {
      slug: "healing-relational-trauma",
      title: "Bridges of Trust: Repairing Relational Wounds",
      excerpt:
        "How early attachment patterns shape our adult relationships, and how secure connections can heal old wounds.",
      category: "Trauma",
      readTime: "7 min read",
      date: "May 10, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=1200&q=80",
      tags: ["Trauma", "Relationships", "Healing"],
      author: {
        name: "Dr. Alisha Sen",
        role: "Clinical Psychologist & Healer",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "Over 12 years of helping individuals heal anxiety, soothe their nervous systems, and build resilient paths to self-discovery.",
        specialization: "Anxiety & Trauma Care",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "Humans are wired for connection. From our earliest days, our survival and emotional health depend on the safety of our relationships. However, when those relationships are marked by inconsistency, emotional neglect, or conflict, we develop defense mechanisms to protect ourselves. These early survival patterns often follow us into adulthood, showing up as anxiety, emotional distance, or difficulty trusting the people we love most.",
        },
        {
          type: "heading",
          level: 2,
          text: "The Echoes of Attachment Wounds",
        },
        {
          type: "paragraph",
          text: "Relational trauma doesn't always stem from a single catastrophic event. Often, it is the result of quiet, repeated experiences of not feeling seen, heard, or protected. When a partner withdraws or when a conflict arises, these old memories can trigger intense physiological reactions. We may react by clinging tightly (anxious attachment) or shutting down and retreating (avoidant attachment). Recognizing these reactions as echoes of the past is the first step toward change.",
        },
        {
          type: "pullquote",
          text: "Relational wounds happen in connection, and they must be healed in connection. We learn to trust again by letting ourselves be seen by those who are safe.",
        },
        {
          type: "heading",
          level: 2,
          text: "Steps to Rebuild Secure Bridges",
        },
        {
          type: "paragraph",
          text: "Healing relational wounds is a gradual, shared process of somatic safety and emotional courage:",
        },
        {
          type: "list",
          items: [
            "Co-Regulation: When a conflict heats up, pause together and take slow, synchronized breaths to calm your nervous systems before speaking.",
            "Clear Expression: Practice expressing your needs from a place of vulnerability rather than blame (e.g., 'I feel anxious when we don't talk' instead of 'You always ignore me').",
            "Consistent Repairs: Repairing doesn't mean never fighting; it means coming back together afterward to validate each other's feelings and reconnect.",
          ],
        },
        {
          type: "paragraph",
          text: "Trust is not built in a single day or through one grand gesture. It is built in the tiny, daily moments of turning toward each other with kindness, presence, and a willing heart.",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "recovering-from-burnout"],
    },
  ],
  hi: [
    {
      slug: "healing-from-anxiety",
      title: "धीमे होने की कला: चिंता से उबरने का एक सचेत मार्ग",
      excerpt:
        "चिंता को हराने वाला दुश्मन नहीं, बल्कि एक संदेशवाहक के रूप में समझें जो आपकी कोमल देखभाल और आंतरिक शांति की मांग करता है।",
      category: "चिंता",
      readTime: "6 मिनट",
      date: "मई 20, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80",
      tags: ["चिंता", "माइंडफुलनेस", "आत्म-देखभाल"],
      author: {
        name: "डॉ. अलीशा सेन",
        role: "क्लिनिकल साइकोलॉजिस्ट और हीलर",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "पिछले 12 वर्षों से लोगों को चिंता से उबरने, उनके नर्वस सिस्टम को शांत करने और आत्म-खोज की राह पर चलने में मदद कर रही हैं।",
        specialization: "चिंता और ट्रॉमा केयर",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "चिंता सबसे आम मानवीय अनुभवों में से एक है, फिर भी इसे सबसे कम समझा जाता है। जब छाती में भारीपन होता है, सांसें तेज होती हैं और विचार अंतहीन लूप में घूमते हैं, तो हमारी तत्काल प्रतिक्रिया लड़ने की होती है। हम उस भावना को दबाना चाहते हैं, ध्यान भटकाना चाहते हैं, या तर्क देना चाहते हैं। लेकिन क्या होगा अगर हम एक अलग रास्ता चुनें? क्या होगा अगर हम चिंता को एक घुसपैठिया न मानकर, एक ऐसा संदेशवाहक समझें जो हमारे दरवाजे पर दस्तक दे रहा है?",
        },
        {
          type: "heading",
          level: 2,
          text: "1. डूबने के बिना लहर का स्वागत करना",
        },
        {
          type: "paragraph",
          text: "चिंता के साथ धीमे होने की शुरुआत अपनी भावनाओं को स्वीकार करने से होती है। जब हम चिंता का विरोध करते हैं, तो हम घबराहट की एक और परत पैदा कर देते हैं—हम चिंतित होने के बारे में चिंतित होने लगते हैं। जब चिंता महसूस हो, तो रुकें और खुद से कहें, 'मैं अभी अपने शरीर में चिंता महसूस कर रहा हूँ, और यह ठीक है।' ऐसा करने से हम खुद को उस भावना से अलग कर पाते हैं। आप खुद चिंता नहीं हैं; आप एक महासागर हैं जिसमें से एक अस्थायी लहर गुजर रही है।",
        },
        {
          type: "pullquote",
          text: "हम तूफान को रोकने की कोशिश नहीं करते। हम बस झोंकों के बीच के शांत स्थानों में आराम करना सीखते हैं, यह जानते हुए कि हर हवा अंततः थम जाती है।",
        },
        {
          type: "heading",
          level: 2,
          text: "2. शरीर और सांसों का तालमेल",
        },
        {
          type: "paragraph",
          text: "हमारा नर्वस सिस्टम अलग-अलग स्तरों पर काम करता है। जब चिंता हावी होती है, तो हमारा 'फाइट-ऑर-फ्लाइट' सिस्टम चालू हो जाता है। संतुलन बहाल करने के लिए, हमें 'रेस्ट-एंड-डाइजेस्ट' सिस्टम को आमंत्रित करना होगा। यह तार्किक सोच से नहीं, बल्कि शरीर के माध्यम से प्राप्त होता है। एक लंबी, गहरी सांस छोड़ना (सांस लेने की तुलना में अधिक देर तक सांस छोड़ना) सीधे आपके मस्तिष्क को संकेत देता है कि आप इस समय पूरी तरह सुरक्षित हैं।",
        },
      ],
      relatedSlugs: ["recovering-from-burnout", "healing-relational-trauma"],
    },
    {
      slug: "somatic-practices-nervous-system",
      title: "सॉमेटिक सुरक्षा: उत्तेजित शरीर को शांत करने के 3 अभ्यास",
      excerpt:
        "शरीर-आधारित तकनीकों का उपयोग करके अपने नर्वस सिस्टम को संतुलित करें और सुरक्षा महसूस करें।",
      category: "सॉमेटिक",
      readTime: "5 मिनट",
      date: "मई 18, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1200&q=80",
      tags: ["सॉमेटिक", "नर्वस सिस्टम", "हीलिंग"],
      author: {
        name: "डॉ. अलीशा सेन",
        role: "क्लिनिकल साइकोलॉजिस्ट और हीलर",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "पिछले 12 वर्षों से लोगों को चिंता से उबरने, उनके नर्वस सिस्टम को शांत करने और आत्म-खोज की राह पर चलने में मदद कर रही हैं।",
        specialization: "चिंता और ट्रॉमा केयर",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "सॉमेटिक थेरेपी हमें हमारे शरीर की भाषा के माध्यम से संवाद करने में मदद करती है। नर्वस सिस्टम को शांत करने के लिए इन तीन अभ्यासों का प्रयास करें।",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "recovering-from-burnout"],
    },
    {
      slug: "recovering-from-burnout",
      title: "बर्नआउट से उबरना: शारीरिक थकान से मानसिक संतुलन की ओर",
      excerpt:
        "बर्नआउट कोई असफलता या कमजोरी नहीं है; यह आपकी आत्मा की गहरी पुकार है जिसे केवल नींद से शांत नहीं किया जा सकता।",
      category: "बर्नआउट",
      readTime: "8 मिनट",
      date: "मई 15, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=1200&q=80",
      tags: ["बर्नआउट", "आराम", "स्वास्थ्य"],
      author: {
        name: "कबीर मेहता, एलसीएसडब्ल्यू",
        role: "सॉमेटिक थेरेपिस्ट और काउंसलर",
        avatarUrl:
          "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=150&q=80",
        bio: "शरीर और मन के तालमेल के माध्यम से नर्वस सिस्टम के संतुलन और तनाव में कमी लाने के विशेषज्ञ।",
        specialization: "सॉमेटिक थेरेपी और बर्नआउट",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "ऐसी दुनिया में जहां लगातार व्यस्त रहना सफलता की निशानी माना जाता है, वहां थकान महसूस करना एक आम बात बन गई है। हम अपनी शारीरिक और मानसिक सीमाओं से परे खुद को धकेलते हैं, जब तक कि एक दिन हमें यह एहसास नहीं होता कि ऊर्जा की लौ बुझ गई है। यही बर्नआउट है। यह सिर्फ एक थका देने वाले सप्ताह के बाद की सामान्य थकान नहीं है; यह खालीपन की एक गहरी स्थिति है जहां वे चीजें भी भारी महसूस होने लगती हैं जिन्हें आप कभी प्यार करते थे।",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "healing-relational-trauma"],
    },
    {
      slug: "mindfulness-beginners-guide",
      title: "शांत स्थान: दैनिक अराजकता में माइंडफुलनेस को एकीकृत करना",
      excerpt:
        "मौन ध्यान के लिए घंटों की आवश्यकता के बिना अपने पूरे दिन में माइंडफुलनेस के छोटे पलों का निर्माण कैसे करें।",
      category: "माइंडफुलनेस",
      readTime: "4 मिनट",
      date: "मई 12, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80",
      tags: ["माइंडफुलनेस", "ध्यान", "शांति"],
      author: {
        name: "कबीर मेहता, एलसीएसडब्ल्यू",
        role: "सॉमेटिक थेरेपिस्ट और काउंसलर",
        avatarUrl:
          "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=150&q=80",
        bio: "शरीर और मन के तालमेल के माध्यम से नर्वस सिस्टम के संतुलन और तनाव में कमी लाने के विशेषज्ञ।",
        specialization: "सॉमेटिक थेरेपी और बर्नआउट",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "माइंडफुलनेस को अक्सर धुंधले पहाड़ की चोटी पर पालथी मारकर बैठे रहने, आँखें बंद रखने और विचारों को पूरी तरह से खाली रखने के रूप में चित्रित किया जाता है। हममें से अधिकांश के लिए, यह संभव नहीं है। सच्ची माइंडफुलनेस के लिए आपको अपने जीवन से भागने की आवश्यकता नहीं है; यह आपसे अपने काम के बीच अपनी पूरी उपस्थिति के साथ जुड़ने के लिए कहती है।",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "recovering-from-burnout"],
    },
    {
      slug: "healing-relational-trauma",
      title: "विश्वास के सेतु: पारस्परिक संबंधों के घावों को भरना",
      excerpt:
        "शुरुआती जुड़ाव के पैटर्न हमारे वयस्क संबंधों को कैसे आकार देते हैं, और सुरक्षित संबंध पुराने घावों को कैसे ठीक कर सकते हैं।",
      category: "आघात",
      readTime: "7 मिनट",
      date: "मई 10, 2026",
      imageUrl:
        "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=1200&q=80",
      tags: ["आघात", "रिश्ते", "हीलिंग"],
      author: {
        name: "डॉ. अलीशा सेन",
        role: "क्लिनिकल साइकोलॉजिस्ट और हीलर",
        avatarUrl:
          "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=150&q=80",
        bio: "पिछले 12 वर्षों से लोगों को चिंता से उबरने, उनके नर्वस सिस्टम को शांत करने और आत्म-खोज की राह पर चलने में मदद कर रही हैं।",
        specialization: "चिंता और ट्रॉमा केयर",
        isVerified: true,
      },
      content: [
        {
          type: "paragraph",
          text: "मनुष्य जुड़ाव के लिए बने हैं। हमारे शुरुआती दिनों से ही हमारा अस्तित्व और भावनात्मक स्वास्थ्य हमारे रिश्तों की सुरक्षा पर निर्भर करता है। हालांकि, जब वे रिश्ते असंगत, उपेक्षित या संघर्षपूर्ण होते हैं, तो हम अपनी रक्षा के लिए तंत्र विकसित करते हैं। ये पैटर्न वयस्क होने पर भी हमारे साथ रहते हैं, जिससे विश्वास करने में कठिनाई होती है।",
        },
      ],
      relatedSlugs: ["healing-from-anxiety", "recovering-from-burnout"],
    },
  ],
};
